/**
 * <scroll-to-top> — native replacement for the XO Scroll-to-Top app.
 *
 * Self-contained Web Component with Shadow DOM. No dependencies.
 * Improvements over XO:
 *   - Respects prefers-reduced-motion (hard-jump instead of smooth scroll)
 *   - Passive scroll listener + requestAnimationFrame throttling
 *   - Focus-visible styles for keyboard users
 *   - ARIA label for screen readers
 *   - CSS variable theming (uses --primary-accent-color from v6)
 *   - No runtime loaded from external CDN
 *   - Built with createElement (no innerHTML, no XSS surface)
 *
 * Usage in layout: <scroll-to-top threshold="300"></scroll-to-top>
 * Attribute `threshold` is the scrollY pixel offset at which the button
 * becomes visible. Defaults to 300.
 */
const SCROLL_TO_TOP_CSS = `
  /* Standard bottom-right position. The Shopify Chat widget is lifted up to
     bottom: 6rem via a !important override in main-style.css (it uses an
     inline style with z-index 2147483647 that nothing else can outrank).
     Visual stack at bottom-right: chat (top) → scroll-to-top (bottom). */
  :host {
    position: fixed;
    bottom: 1.25rem;
    right: 1rem;
    z-index: 50;
    opacity: 0;
    transform: translateY(0.5rem);
    transition: opacity 0.2s ease, transform 0.2s ease;
    pointer-events: none;
  }
  :host(.visible) {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
  @media (prefers-reduced-motion: reduce) {
    :host { transition: none; }
  }
  button {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.85);
    background: var(--primary-accent-color, #8b0e14);
    color: var(--primary-accent-text-color, #ffffff);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.32), 0 2px 4px rgba(0, 0, 0, 0.18);
    padding: 0;
    font: inherit;
  }
  button:hover { filter: brightness(1.1); transform: translateY(-1px); }
  button:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }
  svg { width: 1.4rem; height: 1.4rem; fill: currentColor; display: block; }
`;

const SVG_NS = 'http://www.w3.org/2000/svg';

class ScrollToTop extends HTMLElement {
  constructor() {
    super();
    const root = this.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = SCROLL_TO_TOP_CSS;
    root.appendChild(style);

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', 'Back to top');

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M10 3l-6 6h4v8h4V9h4z');
    svg.appendChild(path);
    button.appendChild(svg);
    root.appendChild(button);

    this.button = button;
    this.threshold = parseInt(this.getAttribute('threshold'), 10) || 300;
    this.ticking = false;
    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.button.addEventListener('click', this.onClick);
    this.updateVisibility();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    this.button.removeEventListener('click', this.onClick);
  }

  onScroll() {
    if (this.ticking) return;
    window.requestAnimationFrame(() => {
      this.updateVisibility();
      this.ticking = false;
    });
    this.ticking = true;
  }

  updateVisibility() {
    const show = window.scrollY > this.threshold;
    this.classList.toggle('visible', show);
  }

  onClick() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('scroll-to-top')) {
  customElements.define('scroll-to-top', ScrollToTop);
}
